import {Alert, Box, Button, CircularProgress, Snackbar, Tooltip, useMediaQuery, useTheme, LinearProgress} from "@mui/material";
import {
    CircleDiv,
    FlexContainer,
    InnerContainerVideo,
    StyledDivCenterButton,
    StyledWebcam,
} from "../../onBoardingVideo/styles";
import Typography from "../../../../components/Typography";
import {ReceiveOnBoardingInviteResponse} from "../../../../shared/models/requests/receive-invite-onboarding";
import {useCallback, useEffect, useState} from "react";
import useConnectivity, {ConnectionQuality} from "../../../../hooks/useConnectivity";
// Substituindo os ícones que não estão disponíveis no MUI por alternativas
import WifiIcon from '@mui/icons-material/Wifi';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import WifiTetheringErrorIcon from '@mui/icons-material/WifiTetheringError';
import TelemetryService from "../../../../shared/services/TelemetryService";
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import styled from "styled-components";
import useEnhancedCamera from "../../../../hooks/useEnhancedCamera";

// Estilização para textos específicos do gravador de vídeo
const WarningText = styled.span`
    background: rgba(255, 152, 0, 0.7);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    display: inline-block;
    font-size: 14px;
    font-weight: 400;
`;

const DurationText = styled.span`
    display: block;
    text-align: center;
    margin-top: 8px;
    font-size: 14px;
    font-weight: 400;
`;

const ProgressContainer = styled.div`
    width: 100%;
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ProgressBar = styled.div<{ progress: number }>`
    width: 100%;
    height: 8px;
    background-color: #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
    position: relative;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: ${props => props.progress}%;
        background-color: #9c27b0;
        transition: width 0.3s ease;
    }
`;

interface VideoRecorderProps {
    handleStart: (previewRef: React.RefObject<HTMLVideoElement>) => void;
    handleStop: (
        mediaRecorder: MediaRecorder | null,
        chunksRef: React.RefObject<BlobPart[]>,
        videoRef: React.RefObject<HTMLVideoElement>,
        previewRef: React.RefObject<HTMLVideoElement>
    ) => void;
    navigateToNext: () => void;
    recording: boolean;
    data?: ReceiveOnBoardingInviteResponse;
    videoRef: React.RefObject<HTMLVideoElement>;
    previewRef: React.RefObject<HTMLVideoElement>;
    chunksRef: React.RefObject<BlobPart[]>;
    mediaRecorder: MediaRecorder | null;
    textPayroll?: string;
    recordingDuration?: number;
    correlationId?: string;
    uploadMetadata?: any;
}

const VideoRecorder: React.FC<VideoRecorderProps> = ({
                                                         handleStart,
                                                         handleStop,
                                                         navigateToNext,
                                                         recording,
                                                         data,
                                                         videoRef,
                                                         previewRef,
                                                         chunksRef,
                                                         mediaRecorder,
                                                         textPayroll,
                                                         recordingDuration = 0,
                                                         correlationId,
                                                         uploadMetadata,
                                                     }: VideoRecorderProps) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const {isOnline, connectionQuality, latency, isConnectionSuitable} = useConnectivity();
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [alertOpen, setAlertOpen] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [videoUrl, setVideoUrl] = useState<string>("");
    const {
        hasCamera,
        hasMicrophone,
        checkDevicePermissions,
        initializeDevices,
        uploadVideo,
        uploading,
    } = useEnhancedCamera();

    // Log do início da interação com o componente
    useEffect(() => {
        TelemetryService.info(
            "VideoRecorder",
            "component_mounted",
            {screenSize: isSmallScreen ? "small" : "large", correlationId},
            correlationId
        );

        // Verificar permissões de dispositivos e inicializar
        initializeDevices();
    }, [isSmallScreen, correlationId, initializeDevices]);

    useEffect(() => {
        setErrorMessage('')
        // Se algum dos dispositivos não estiver disponível, mostrar alerta
        if (!hasCamera || !hasMicrophone) {
            setErrorMessage(
                !hasCamera && !hasMicrophone
                    ? "Câmera e microfone não detectados. Verifique as permissões do navegador."
                    : !hasCamera
                        ? "Câmera não detectada. Verifique as permissões do navegador."
                        : "Microfone não detectado. Verifique as permissões do navegador."
            );
            setAlertOpen(true);

            TelemetryService.warn(
                "VideoRecorder",
                "device_permission_issues",
                {hasCamera, hasMicrophone},
                correlationId
            );
        }
    }, [hasCamera, hasMicrophone, correlationId]);

    // Monitora a qualidade da conexão durante a gravação
    useEffect(() => {
        if (recording && connectionQuality === 'low') {
            TelemetryService.warn(
                "VideoRecorder",
                "low_connection_quality_while_recording",
                {connectionQuality, latency},
                correlationId
            );
        }
    }, [connectionQuality, recording, latency, correlationId]);

    // Atualizar videoUrl quando o vídeo estiver pronto
    useEffect(() => {
        if (videoRef.current && videoRef.current.src) {
            setVideoUrl(videoRef.current.src);
        }
    }, [videoRef]);

    const getConnectionIcon = (quality: ConnectionQuality) => {
        if (!isOnline) return <SignalWifiOffIcon color="error"/>;
        switch (quality) {
            case 'high':
                return <WifiIcon color="success"/>;
            case 'medium':
                return <WifiIcon color="primary"/>;
            case 'low':
                return <WifiTetheringErrorIcon color="warning"/>;
            default:
                return <WifiTetheringErrorIcon color="action"/>;
        }
    };

    const getConnectionTooltip = (quality: ConnectionQuality) => {
        if (!isOnline) return "Sem conexão. O vídeo será salvo localmente até que a conexão seja restaurada.";
        switch (quality) {
            case 'high':
                return "Conexão excelente para gravação e envio de vídeo";
            case 'medium':
                return "Conexão boa para gravação de vídeo";
            case 'low':
                return "Conexão lenta. O vídeo pode levar mais tempo para ser enviado.";
            default:
                return "Qualidade da conexão desconhecida";
        }
    };
    const handleRecordButton = useCallback(async () => {
        if (recording) {
            setIsProcessing(true);
            TelemetryService.info(
                "VideoRecorder",
                "video_record_stop",
                {
                    duration: recordingDuration,
                    connectionQuality,
                },
                correlationId
            );

            try {
                handleStop(mediaRecorder, chunksRef, videoRef, previewRef);

                // Pequeno delay para feedback visual
                setTimeout(() => {
                    setIsProcessing(false);
                    navigateToNext();
                }, 500);
            } catch (error) {
                console.error("Erro ao parar gravação:", error);
                setErrorMessage("Ocorreu um erro ao finalizar a gravação. Tente novamente.");
                setAlertOpen(true);
                setIsProcessing(false);

                TelemetryService.error(
                    "VideoRecorder",
                    "video_record_stop_error",
                    {error: String(error)},
                    correlationId
                );
            }
        } else {
            const permissionsGranted = await checkDevicePermissions();

            if (!permissionsGranted) {
                setErrorMessage("Permissões de câmera/microfone necessárias para iniciar a gravação.");
                setAlertOpen(true);
                return;
            }

            try {
                // Log de telemetria para iniciar gravação
                TelemetryService.info(
                    "VideoRecorder",
                    "video_record_start",
                    {connectionQuality, latency},
                    correlationId
                );

                handleStart(previewRef);
            } catch (error) {
                console.error("Erro ao iniciar gravação:", error);
                setErrorMessage("Ocorreu um erro ao iniciar a gravação. Verifique as permissões do seu navegador.");
                setAlertOpen(true);

                TelemetryService.error(
                    "VideoRecorder",
                    "video_record_start_error",
                    {error: String(error)},
                    correlationId
                );
            }
        }
    }, [
        recording, recordingDuration, connectionQuality, correlationId,
        handleStop, mediaRecorder, chunksRef, videoRef, previewRef, navigateToNext,
        checkDevicePermissions, isConnectionSuitable, handleStart
    ]);

    const handleUploadProgress = useCallback((progress: any) => {
        setUploadProgress(progress.percentage || 0);
    }, []);

    const handleUploadButton = useCallback(async () => {
        if (!mediaRecorder || !chunksRef || !videoRef || !previewRef) {
            setErrorMessage("Não foi possível enviar o vídeo. Gravação não inicializada corretamente.");
            setAlertOpen(true);
            return;
        }

        try {
            TelemetryService.info(
                "VideoRecorder",
                "video_upload_start",
                { connectionQuality },
                correlationId
            );

            const result = await uploadVideo({
                ...uploadMetadata,
                // adicionar outros metadados necessários
            });

            if (result.success) {
                navigateToNext();
            }
        } catch (error) {
            console.error("Erro ao enviar vídeo:", error);
            setErrorMessage("Ocorreu um erro ao enviar o vídeo. Tente novamente mais tarde.");
            setAlertOpen(true);

            TelemetryService.error(
                "VideoRecorder",
                "video_upload_error",
                { error: String(error) },
                correlationId
            );
        }
    }, [
        mediaRecorder, chunksRef, videoRef, previewRef, uploadVideo,
        connectionQuality, correlationId, navigateToNext, uploadMetadata
    ]);

    return (
        <>
            <FlexContainer>
                <Box sx={{width: '100%', position: 'relative'}}>
                    <Typography variant="caption" align="center">
                        {textPayroll || data?.inviteDTO.telepromptText}
                    </Typography>
                    <InnerContainerVideo>
                        {recording && <Tooltip title={"Gravando..."}>
                            <Box
                                sx={{
                                    zIndex: 9999,
                                    marginTop: '1%',
                                    position: 'absolute',
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    bgcolor: 'error.main',
                                    animation: 'pulse 1.5s infinite',
                                    '@keyframes pulse': {
                                        '0%': {opacity: 1},
                                        '50%': {opacity: 0.4},
                                        '100%': {opacity: 1},
                                    },
                                }}
                            />
                        </Tooltip>}
                        <CircleDiv isSmallScreen={isSmallScreen}>
                            <StyledWebcam audio={false} mirrored={true} muted={true}/>
                        </CircleDiv>
                    </InnerContainerVideo>
                </Box>
            </FlexContainer>
            <StyledDivCenterButton>
                <Box>
                    <Tooltip title={hasCamera ? "Câmera ativa" : "Câmera não detectada"}>
                        {hasCamera ? <VideocamIcon color="success"/> : <VideocamOffIcon color="error"/>}
                    </Tooltip>

                    <Tooltip title={hasMicrophone ? "Microfone ativo" : "Microfone não detectado"}>
                        {hasMicrophone ? <MicIcon color="success"/> : <MicOffIcon color="error"/>}
                    </Tooltip>

                    <Tooltip title={getConnectionTooltip(connectionQuality)} placement="top">
                        {getConnectionIcon(connectionQuality)}
                    </Tooltip>
                </Box>
            </StyledDivCenterButton>
            <StyledDivCenterButton>
                <Button
                    onClick={handleRecordButton}
                    color="primary"
                    variant={"contained"}
                    disabled={isProcessing || uploading}
                    startIcon={isProcessing && <CircularProgress size={16} color="inherit"/>}
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        '&::after': recording ? {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '100%',
                            height: '3px',
                            backgroundColor: theme.palette.warning.main,
                            animation: 'progress 60s linear',
                            transformOrigin: 'left',
                            '@keyframes progress': {
                                '0%': {transform: 'scaleX(0)'},
                                '100%': {transform: 'scaleX(1)'}
                            }
                        } : {}
                    }}
                >
                    {isProcessing
                        ? "Processando..."
                        : (recording ? "Parar gravação" : "Iniciar gravação")}
                </Button>

                {/* Botão de upload quando o vídeo estiver pronto */}
                {videoUrl && !recording && !isProcessing && (
                    <Button
                        onClick={handleUploadButton}
                        color="secondary"
                        variant={"contained"}
                        disabled={uploading}
                        startIcon={uploading ? <CircularProgress size={16} color="inherit"/> : <CloudUploadIcon />}
                        sx={{ ml: 2 }}
                    >
                        {uploading ? "Enviando..." : "Enviar vídeo"}
                    </Button>
                )}
            </StyledDivCenterButton>

            {/* Mostrar progresso de upload */}
            {uploading && (
                <ProgressContainer>
                    <Typography variant="caption">
                        {`Enviando vídeo: ${Math.round(uploadProgress)}%`}
                    </Typography>
                    <Box sx={{ width: '80%', mt: 1 }}>
                        <ProgressBar progress={uploadProgress} />
                    </Box>
                </ProgressContainer>
            )}

            <Snackbar
                open={alertOpen && !!errorMessage}
                autoHideDuration={6000}
                onClose={() => setAlertOpen(false)}
                anchorOrigin={{vertical: 'top', horizontal: 'center'}}
            >
                <Alert
                    onClose={() => setAlertOpen(false)}
                    severity="warning"
                    sx={{width: '100%'}}
                >
                    {errorMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default VideoRecorder;
